/**
 * W3 — the search slice.
 *
 * One store serves both fields: the chat list's (global) and the thread's (one chat) — the frozen
 * contract says they are the same endpoint, and `scope` is the only thing that differs. The store
 * answers what it is asked and never debounces (the field owns the timing, ~250 ms) and it guards
 * its own answers: the api echoes the query it ran, so a response that arrives after a newer
 * request — or for a chat the reader has left — is dropped instead of shown.
 */
import { create } from 'zustand'
import type { SearchHit } from '@olmt/shared'
import { api } from '../lib/api'
import { mergeHits, SEARCH_MAX_LIMIT, SEARCH_PAGE_SIZE } from '../lib/search'

interface SearchState {
  /** The trimmed query the hits belong to; `''` while nothing has been asked. */
  query: string
  /** `null` searches every chat the caller is in (the list's field); otherwise one chat's id. */
  scope: string | null
  hits: SearchHit[]
  /**
   * The `limit` the last request asked for. The api has no search cursor, so a wider `limit` is the
   * only way further into a result set — what "More" grows, and what `SEARCH_MAX_LIMIT` caps.
   */
  limit: number
  loading: boolean
  error: boolean
  /** The api's answer: more matches exist than this page holds. */
  hasMore: boolean
  /** Ask for a fresh first page. Trims; a blank query is the caller's to reject (`isSearchableQuery`). */
  run: (query: string, scope: string | null) => Promise<void>
  /** Fold the next page in. A no-op while loading, when nothing more exists, or at the api's ceiling. */
  loadMore: () => Promise<void>
  /** Drop everything — the field's Clear, and every path that ends a session (see `signOut`). */
  clear: () => void
}

/** Bumped by every request: an answer whose id is not the newest is stale, whatever it carries. */
let requestId = 0

export const useSearchStore = create<SearchState>()((set, get) => ({
  query: '',
  scope: null,
  hits: [],
  limit: SEARCH_PAGE_SIZE,
  loading: false,
  error: false,
  hasMore: false,

  run: async (query, scope) => {
    const trimmed = query.trim()
    const id = ++requestId
    set({
      query: trimmed,
      scope,
      hits: [],
      limit: SEARCH_PAGE_SIZE,
      loading: true,
      error: false,
      hasMore: false,
    })
    try {
      const response = await api.search({ q: trimmed, chatId: scope ?? undefined, limit: SEARCH_PAGE_SIZE })
      // Stale three ways: a newer request started, the field holds another query, or the reader
      // moved to another chat. The echoed query is the api's own copy of what it actually ran.
      if (id !== requestId || response.query !== get().query || get().scope !== scope) return
      set({ hits: response.hits, hasMore: response.hasMore, loading: false })
    } catch {
      if (id !== requestId) return
      set({ loading: false, error: true })
    }
  },

  loadMore: async () => {
    const { query, scope, limit, hasMore, loading } = get()
    if (!hasMore || loading || query === '' || limit >= SEARCH_MAX_LIMIT) return
    const id = requestId
    const next = Math.min(limit + SEARCH_PAGE_SIZE, SEARCH_MAX_LIMIT)
    set({ limit: next, loading: true, error: false })
    try {
      const response = await api.search({ q: query, chatId: scope ?? undefined, limit: next })
      if (id !== requestId || response.query !== get().query || get().scope !== scope) return
      set((current) => ({
        hits: mergeHits(current.hits, response.hits),
        hasMore: response.hasMore,
        loading: false,
      }))
    } catch {
      if (id !== requestId) return
      // The width the reader asked for comes back with the failure, so a retry asks for the same
      // page rather than silently skipping one.
      set({ limit, loading: false, error: true })
    }
  },

  clear: () => {
    requestId++
    set({
      query: '',
      scope: null,
      hits: [],
      limit: SEARCH_PAGE_SIZE,
      loading: false,
      error: false,
      hasMore: false,
    })
  },
}))
